import { useState, useMemo } from 'react'
import { Icon } from '@iconify/react'
import { VIOLATION_IMPACT_COLORS } from '../../utils/constants'
import LoadingSpinner from '../common/LoadingSpinner'
import Select from '../common/Select'

const formatFixedAt = (dateString) => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

const ImpactBadge = ({ impact }) => {
  const colors = VIOLATION_IMPACT_COLORS[impact] || 'text-gray-600 bg-gray-50'
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded capitalize ${colors}`}>
      {impact}
    </span>
  )
}

const InlineState = ({ icon, title, message, actionLabel, onAction }) => (
  <div className="text-center py-12 px-4 rounded-xl border border-dashed border-border-light bg-gray-50">
    <Icon icon={icon} className="text-4xl mx-auto mb-3 text-gray-400" />
    <p className="text-sm font-semibold text-text-dark">{title}</p>
    <p className="text-sm text-text-secondary mt-1">{message}</p>
    {actionLabel && (
      <button
        type="button"
        onClick={onAction}
        className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-hover transition-colors"
      >
        <Icon icon="mdi:refresh" />
        {actionLabel}
      </button>
    )}
  </div>
)

const RemediationEntry = ({ entry, isExpanded, onToggle }) => {
  const summaryLine = `Issue #${entry.issueNumber ?? entry.id}: ${entry.violationType ?? entry.type ?? 'Violation'} on ${entry.page ?? entry.url ?? '—'} — Fixed ${formatFixedAt(entry.fixedAt)}`
  const fixedBy = entry.fixedBy ?? entry.resolvedBy ?? 'Auto-fix'

  return (
    <div className="bg-white rounded-xl border border-border-light shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-4 md:p-5 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-green-50 border border-green-200 flex items-center justify-center shrink-0">
            <Icon icon="mdi:check-circle" className="text-green-600 text-xl" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-text-dark leading-relaxed">{summaryLine}</p>

            <div className="flex flex-wrap items-center gap-2 mt-2">
              {entry.impact && <ImpactBadge impact={entry.impact} />}
              {(entry.websiteName || entry.website) && (
                <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                  <Icon icon="mdi:web" className="text-sm" />
                  {entry.websiteName ?? entry.website}
                </span>
              )}
              <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                <Icon icon={fixedBy.toLowerCase().includes('auto') ? 'mdi:robot' : 'mdi:account-edit'} />
                {fixedBy}
              </span>
              {(entry.scanId ?? entry.scan) && (
                <span className="text-xs font-mono text-gray-400">{entry.scanId ?? entry.scan}</span>
              )}
            </div>
          </div>

          <Icon
            icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
            className="text-gray-400 text-xl shrink-0"
          />
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 md:px-5 pb-5 border-t border-gray-100 bg-gray-50/50 animate-fade-in">
          {entry.changeDescription && (
            <>
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mt-4 mb-3">
                What Changed
              </p>
              <p className="text-sm text-text-dark mb-4">{entry.changeDescription}</p>
            </>
          )}

          {(entry.before || entry.after) && (
            <div className="grid md:grid-cols-2 gap-3">
              {entry.before && (
                <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                  <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1">
                    <Icon icon="mdi:close-circle-outline" />
                    Before
                  </p>
                  <code className="text-xs text-red-900 break-all font-mono block leading-relaxed">
                    {entry.before}
                  </code>
                </div>
              )}
              {entry.after && (
                <div className="rounded-lg border border-green-200 bg-green-50/50 p-3">
                  <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                    <Icon icon="mdi:check-circle-outline" />
                    After
                  </p>
                  <code className="text-xs text-green-900 break-all font-mono block leading-relaxed">
                    {entry.after}
                  </code>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
            {(entry.fullUrl ?? entry.url) && (
              <span className="inline-flex items-center gap-1">
                <Icon icon="mdi:link-variant" />
                <a
                  href={entry.fullUrl ?? entry.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {entry.fullUrl ?? entry.url}
                </a>
              </span>
            )}
            {(entry.rule ?? entry.ruleId) && (
              <span className="inline-flex items-center gap-1">
                <Icon icon="mdi:code-tags" />
                Rule: {entry.rule ?? entry.ruleId}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const RemediationLogSection = ({
  remediations = [],
  isLoading = false,
  error = null,
  onRetry,
  currentPage = 1,
  totalPages = 1,
  totalResults = 0,
  onPageChange,
}) => {
  const [search, setSearch] = useState('')
  const [impactFilter, setImpactFilter] = useState('all')
  const [expandedId, setExpandedId] = useState(null)

  const filteredEntries = useMemo(() => {
    return remediations.filter((entry) => {
      const violationType = entry.violationType ?? entry.type ?? ''
      const page = entry.page ?? entry.url ?? ''
      const issueNumber = String(entry.issueNumber ?? entry.id ?? '')

      const matchesSearch =
        !search ||
        violationType.toLowerCase().includes(search.toLowerCase()) ||
        page.toLowerCase().includes(search.toLowerCase()) ||
        issueNumber.includes(search)

      const matchesImpact = impactFilter === 'all' || entry.impact === impactFilter

      return matchesSearch && matchesImpact
    })
  }, [remediations, search, impactFilter])

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-text-dark">Remediation Log</h2>
        <p className="text-sm text-text-secondary mt-0.5">
          A complete record of every violation fixed — what changed, when, and on which page.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Icon
            icon="mdi:magnify"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search by issue #, violation type, or page..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={isLoading}
            className="form-control pl-10"
          />
        </div>
        <Select
          value={impactFilter}
          onChange={(e) => setImpactFilter(e.target.value)}
          disabled={isLoading}
          className="w-full sm:w-40"
          wrapperClassName=""
        >
          <option value="all">All impacts</option>
          <option value="critical">Critical</option>
          <option value="serious">Serious</option>
          <option value="moderate">Moderate</option>
          <option value="minor">Minor</option>
        </Select>
      </div>

      <div className="space-y-3">
        {error && (
          <InlineState
            icon="mdi:alert-circle-outline"
            title="Remediation log could not be loaded"
            message="We could not fetch fixed violation records right now. Please try again."
            actionLabel="Retry"
            onAction={onRetry}
          />
        )}

        {isLoading && remediations.length === 0 && (
          <div className="py-12 rounded-xl border border-border-light bg-white">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {!isLoading && !error && filteredEntries.length === 0 && remediations.length === 0 && (
          <InlineState
            icon="mdi:clipboard-text-off-outline"
            title="No remediation entries found"
            message="No violations have been fixed for this website yet."
          />
        )}

        {!isLoading && !error && filteredEntries.length === 0 && remediations.length > 0 && (
          <InlineState
            icon="mdi:clipboard-text-off-outline"
            title="No results match your filters"
            message="Try adjusting the search or impact filter."
          />
        )}

        {!error && filteredEntries.map((entry) => (
          <RemediationEntry
            key={entry.id ?? entry._id}
            entry={entry}
            isExpanded={expandedId === (entry.id ?? entry._id)}
            onToggle={() => {
              const id = entry.id ?? entry._id
              setExpandedId(expandedId === id ? null : id)
            }}
          />
        ))}
      </div>

      {!error && totalPages > 1 && (
        <div className="mt-4 px-3 py-2 border-t border-gray-200 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Icon icon="mdi:chevron-left" />
            Prev
          </button>
          <span className="text-xs text-gray-500">
            Page {currentPage} of {totalPages}
            <span className="text-gray-400 ml-1">({totalResults} total)</span>
          </span>
          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isLoading}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
            <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      )}
    </div>
  )
}

export default RemediationLogSection
